import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, HardDrive, Loader2, UploadCloud } from "lucide-react";
import { api, errMsg, fmtBytes } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const MODES = {
  docs: "Documents utiles seulement (PDF, images, Office, texte)",
  none: "Aucune analyse pendant l'import",
  all: "Tout analyser",
};

function Usage({ s }) {
  const ovh = s.backend === "ovh";
  const used = s.usage.emergent.bytes;
  const pct = Math.min(100, Math.round((used / s.emergent_quota_bytes) * 100));
  return (
    <div className="bg-white border rounded-xl p-5 space-y-3" data-testid="storage-usage">
      <div className="text-sm font-semibold flex items-center gap-2"><HardDrive className="h-4 w-4 text-[#1E3A8A]" />Espace utilisé</div>
      <div className="text-sm text-slate-600">
        Stockage actif : <b data-testid="storage-backend">{ovh ? "OVH Object Storage (votre espace)" : "Stockage inclus Emergent (5 Go)"}</b>
      </div>
      <div>
        <div className="flex justify-between text-xs text-slate-500 mb-1">
          <span>Stockage inclus : {fmtBytes(used)} sur 5 Go</span><span>{pct} %</span>
        </div>
        <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
          <div className={`h-full ${pct > 80 ? "bg-red-500" : "bg-[#1E3A8A]"}`} style={{ width: `${pct}%` }} />
        </div>
      </div>
      {ovh && <div className="text-sm text-slate-600" data-testid="storage-ovh-usage">OVH : {fmtBytes(s.usage.ovh.bytes)} ({s.usage.ovh.versions} fichiers et versions)</div>}
    </div>
  );
}

function Migration({ s, reload }) {
  const m = s.migration || {};
  const running = m.status === "running";
  const [busy, setBusy] = useState(false);
  const run = async (path, ok) => {
    setBusy(true);
    try { const { data } = await api.post(path); toast.success(ok(data)); reload(); } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  if (s.backend !== "ovh") {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 text-sm text-amber-900" data-testid="storage-ovh-missing">
        Le stockage OVH n'est pas encore branché. Dès que les accès OVH sont saisis, tous les nouveaux fichiers y seront enregistrés.
      </div>
    );
  }
  return (
    <div className="bg-white border rounded-xl p-5 space-y-3" data-testid="storage-migration">
      <div className="text-sm font-semibold flex items-center gap-2"><UploadCloud className="h-4 w-4 text-[#1E3A8A]" />Transfert des anciens fichiers vers OVH</div>
      <p className="text-sm text-slate-600">{fmtBytes(s.usage.emergent.bytes)} restent dans le stockage inclus. Le transfert se fait en arrière-plan, les fichiers restent consultables pendant l'opération.</p>
      <div className="flex flex-wrap gap-2 items-center">
        <Button variant="outline" disabled={busy} onClick={() => run("/admin/storage/check", () => "Connexion OVH vérifiée")} data-testid="storage-check-btn">
          <CheckCircle2 className="h-4 w-4 mr-2" />Tester la connexion OVH
        </Button>
        <Button disabled={busy || running || !s.usage.emergent.versions} onClick={() => run("/admin/storage/migrate", () => "Transfert lancé")} data-testid="storage-migrate-btn">
          {running && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Transférer vers OVH
        </Button>
        {m.status && m.status !== "idle" && (
          <span className="text-xs text-slate-500" data-testid="storage-migration-status">
            {running ? "En cours" : m.status === "done" ? "Terminé" : m.status === "interrupted" ? "Interrompu, relancez" : m.status}
            {m.total ? ` · ${m.done || 0} / ${m.total} fichiers` : ""}{m.errors ? ` · ${m.errors} erreur(s)` : ""}
          </span>
        )}
      </div>
    </div>
  );
}

export function AdminStorage() {
  const [s, setS] = useState(null);
  const load = useCallback(() => api.get("/admin/storage").then((r) => setS(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (s?.migration?.status !== "running") return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [s?.migration?.status, load]);
  if (!s) return null;
  const setMode = async (mode) => {
    try { await api.put("/admin/storage/import-analysis", { mode }); toast.success("Réglage enregistré"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-6" data-testid="admin-storage">
      <div className="grid lg:grid-cols-2 gap-6">
        <Usage s={s} />
        <Migration s={s} reload={load} />
      </div>
      <div className="bg-white border rounded-xl p-5 space-y-3" data-testid="import-analysis-card">
        <div className="text-sm font-semibold">Analyse IA des gros imports</div>
        <p className="text-sm text-slate-600">S'applique aux fichiers envoyés par l'outil de synchronisation PC et par « Déposer un dossier ».
          Les fichiers non analysés restent stockés et consultables ; ouvrez un dossier puis « Analyser ce dossier » pour les traiter plus tard.</p>
        <Select value={s.import_analysis} onValueChange={setMode}>
          <SelectTrigger className="max-w-md" data-testid="import-analysis-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {Object.entries(MODES).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`import-analysis-${k}`}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
